import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardCheck, AlertTriangle, Phone, TrendingUp, Award,
  MapPin, Copy, Share2, CheckCircle2, ArrowRight, Loader2, Moon,
} from 'lucide-react';
import { toast } from 'sonner';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useFM } from '@/context/FMContext';
import { axiosInstance } from '@/App';
import { formatCurrency } from '@/lib/fmUtils';

/**
 * Navigation targets for carry-forward items.
 */
const CARRY_FORWARD_NAV = {
  sop: '/fm/sop',
  escalation: '/fm/escalations',
  pipeline: '/fm/pipeline',
};

/**
 * Icons for carry-forward item types.
 */
const CARRY_FORWARD_ICONS = {
  sop: ClipboardCheck,
  escalation: AlertTriangle,
  pipeline: Phone,
};

/**
 * Get score background color based on rating.
 */
function getScoreBg(rating) {
  switch (rating) {
    case 'Excellent':
      return 'bg-green-50';
    case 'Good':
      return 'bg-blue-50';
    case 'Needs Improvement':
      return 'bg-yellow-50';
    case 'Critical':
      return 'bg-red-50';
    default:
      return 'bg-slate-50';
  }
}

/**
 * Get score text color based on rating.
 */
function getScoreTextColor(rating) {
  switch (rating) {
    case 'Excellent':
      return 'text-green-600';
    case 'Good':
      return 'text-blue-600';
    case 'Needs Improvement':
      return 'text-yellow-600';
    case 'Critical':
      return 'text-red-600';
    default:
      return 'text-slate-600';
  }
}

/**
 * Get progress bar color based on rating.
 */
function getProgressColor(rating) {
  switch (rating) {
    case 'Excellent':
      return 'bg-green-500';
    case 'Good':
      return 'bg-blue-500';
    case 'Needs Improvement':
      return 'bg-yellow-500';
    case 'Critical':
      return 'bg-red-500';
    default:
      return 'bg-slate-400';
  }
}

/**
 * Format a highlight value based on its format type.
 */
function formatHighlightValue(highlight) {
  if (highlight.format === 'currency') {
    return formatCurrency(highlight.value);
  }
  if (highlight.format === 'count') {
    return `${highlight.value} completed`;
  }
  // Default: raw value (e.g. "18/22")
  return highlight.value;
}

/**
 * Generate shareable text from EOD report data.
 */
function generateShareableText(data, gymName) {
  const lines = [];
  const dateStr = data.date || new Date().toISOString().slice(0, 10);
  lines.push(`${gymName} - End of Day Report (${dateStr})`);
  lines.push('');

  const score = data.score || {};
  lines.push(`Score: ${score.completion_pct || 0}% - ${score.rating || 'N/A'}`);
  lines.push(`Tasks: ${score.tasks_completed || 0}/${score.tasks_total || 0} completed`);
  lines.push('');

  lines.push('Highlights:');
  (data.highlights || []).forEach((h) => {
    lines.push(`- ${h.label}: ${formatHighlightValue(h)}`);
  });
  lines.push('');

  const cf = data.carry_forward || [];
  if (cf.length > 0) {
    lines.push("Tomorrow's Carry-Forward:");
    cf.forEach((item) => {
      lines.push(`- ${item.description}`);
    });
  } else {
    lines.push('No carry-forward items. Clean slate for tomorrow!');
  }

  return lines.join('\n');
}

/**
 * FMEndOfDay - End-of-Day Report Card Page.
 * Visual score with percentage/rating, highlights grid, carry-forward, and share capability.
 */
export default function FMEndOfDay() {
  const { selectedGymId, selectedGym } = useFM();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!selectedGymId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`/fm/eod/${selectedGymId}`);
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load report card');
    } finally {
      setLoading(false);
    }
  }, [selectedGymId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleRefresh = async () => {
    await fetchReport();
  };

  const gymName = selectedGym?.name || selectedGym?.gym_name || 'Gym';

  const handleCopy = async () => {
    if (!report) return;
    try {
      const text = generateShareableText(report, gymName);
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Report copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleWhatsApp = () => {
    if (!report) return;
    const text = generateShareableText(report, gymName);
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  // No gym selected
  if (!selectedGymId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Moon className="w-12 h-12 text-slate-300 mb-3" />
        <p className="text-slate-500 text-sm">Select a gym to view report card</p>
      </div>
    );
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-4">
        {/* Score skeleton */}
        <div className="bg-white rounded-2xl border p-8 animate-pulse text-center">
          <div className="h-4 bg-slate-200 rounded w-1/3 mx-auto mb-4" />
          <div className="h-12 bg-slate-200 rounded w-1/4 mx-auto mb-3" />
          <div className="h-4 bg-slate-200 rounded w-1/5 mx-auto mb-4" />
          <div className="h-3 bg-slate-100 rounded w-3/4 mx-auto mb-2" />
          <div className="h-3 bg-slate-100 rounded w-1/2 mx-auto" />
        </div>
        {/* Highlights skeleton */}
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg border p-3 animate-pulse text-center">
              <div className="h-3 bg-slate-200 rounded w-2/3 mx-auto mb-2" />
              <div className="h-5 bg-slate-200 rounded w-1/2 mx-auto mb-1" />
              <div className="h-2 bg-slate-100 rounded w-3/4 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-700 text-sm">{error}</p>
        <button onClick={fetchReport} className="mt-2 text-sm text-red-600 underline">
          Retry
        </button>
      </div>
    );
  }

  const score = report?.score || {};
  const highlights = report?.highlights || [];
  const carryForward = report?.carry_forward || [];
  const rating = score.rating || 'N/A';
  const completionPct = score.completion_pct || 0;

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      pullingContent=""
      refreshingContent={
        <div className="text-center py-2 text-sm text-slate-500">Refreshing...</div>
      }
    >
      <div className="space-y-4 pb-20">
        {/* Score section */}
        <div className={`rounded-2xl border p-6 text-center shadow-sm ${getScoreBg(rating)}`}>
          <p className="text-sm font-medium text-slate-500 mb-2">Today's Score</p>

          {/* Large percentage */}
          <p className={`text-5xl font-bold ${getScoreTextColor(rating)}`}>
            {completionPct}%
          </p>

          {/* Rating text */}
          <p className={`text-lg font-semibold mt-1 ${getScoreTextColor(rating)}`}>
            {rating}
          </p>

          {/* Progress bar */}
          <div className="mt-4 mx-auto max-w-xs">
            <div className="w-full h-3 bg-white/70 rounded-full overflow-hidden">
              <div
                className={`h-full ${getProgressColor(rating)} rounded-full transition-all duration-500`}
                style={{ width: `${completionPct}%` }}
              />
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {score.tasks_completed || 0}/{score.tasks_total || 0} tasks completed
            </p>
          </div>
        </div>

        {/* Highlights grid */}
        {highlights.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {highlights.map((h, idx) => (
              <div
                key={idx}
                className="rounded-lg border p-3 bg-white text-center shadow-sm"
              >
                <p className="text-xs text-slate-500 font-medium mb-1">{h.label}</p>
                <p className="text-base font-bold text-slate-800">
                  {h.format === 'currency'
                    ? formatCurrency(h.value)
                    : h.value}
                </p>
                {h.pct != null && (
                  <div className="mt-1.5 mx-auto w-full max-w-[100px]">
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          h.pct >= 80 ? 'bg-green-500' : h.pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(h.pct, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
                {h.format === 'currency' && (
                  <p className="text-xs text-slate-400 mt-0.5">added today</p>
                )}
                {h.format === 'count' && (
                  <p className="text-xs text-slate-400 mt-0.5">completed</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Carry-forward section */}
        {carryForward.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-amber-800 mb-3">
              Tomorrow's Carry-Forward
            </h3>
            <div className="space-y-2">
              {carryForward.map((item, idx) => {
                const Icon = CARRY_FORWARD_ICONS[item.type] || ClipboardCheck;
                const navTarget = CARRY_FORWARD_NAV[item.type];

                return (
                  <button
                    key={idx}
                    onClick={() => navTarget && navigate(navTarget)}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white/70 hover:bg-white active:scale-[0.98] transition-all text-left"
                  >
                    <Icon className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span className="text-sm text-amber-900 flex-1">{item.description}</span>
                    {navTarget && (
                      <ArrowRight className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {carryForward.length === 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-1" />
            <p className="text-sm text-green-700 font-medium">
              No carry-forward items. Clean slate for tomorrow!
            </p>
          </div>
        )}

        {/* Bottom actions */}
        <div className="space-y-3">
          {/* Share buttons */}
          <div className="flex gap-3">
            {/* Copy report */}
            <button
              onClick={handleCopy}
              className={`flex-1 h-12 rounded-lg border font-medium text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-all ${
                copied
                  ? 'bg-green-50 border-green-300 text-green-700'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {copied ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Share Report
                </>
              )}
            </button>

            {/* WhatsApp share */}
            <button
              onClick={handleWhatsApp}
              className="flex-1 h-12 rounded-lg bg-green-500 hover:bg-green-600 text-white font-medium text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>

          {/* Start Tomorrow button */}
          <button
            onClick={() => navigate('/fm')}
            className="w-full h-12 rounded-lg bg-slate-800 hover:bg-slate-900 text-white font-medium text-sm flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
          >
            <ArrowRight className="w-4 h-4" />
            Start Tomorrow
          </button>
        </div>
      </div>
    </PullToRefresh>
  );
}
